import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material"
import { forEach } from "lodash"
import type { DeepKeys } from "@tanstack/react-form"
import { SETUP_GOALS, SetupGoal } from "connected-components/optimize-experiments/constants"
import { useFormContext } from "connected-components/optimize-experiments/form-context"
import {
  computeGoalValue,
  GoalRadio,
  type GoalValue,
} from "connected-components/optimize-experiments/components"
import type { FormSections } from "connected-components/optimize-experiments/configuration-sections"
import { targetTotalRiskSchema } from "connected-components/optimize-experiments/form-validation-schema"

import "./style.scss"

interface SetupProps {
  currentTotalRiskValue: number | undefined
  totalRiskStatus: string
}

interface GoalConfig {
  fieldName?: DeepKeys<FormSections>
  label: string
  valueUnit: string
}

const GOAL_SELECTION_OPTIONS: Record<SetupGoal, GoalConfig> = {
  [SETUP_GOALS.TARGET_TOTAL_RISK]: {
    fieldName: "setup.targetTotalRiskValue",
    label: "Target Total Risk",
    valueUnit: "%",
  },
  [SETUP_GOALS.MIN_VOLATILITY]: {
    label: "Min Volatility",
    valueUnit: "%",
  },
  [SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK]: {
    label: "Maximize Specific Risk",
    valueUnit: "%",
  },
}

export default function Setup({
  currentTotalRiskValue,
  totalRiskStatus,
}: SetupProps) {
  const form = useFormContext()
  const currentTotalRiskGoalValue: GoalValue = computeGoalValue(currentTotalRiskValue, totalRiskStatus)

  const clearAllGoalValues = () => {
    forEach(GOAL_SELECTION_OPTIONS, (option: GoalConfig) => {
      if (option.fieldName) {
        form.setFieldValue(option.fieldName, undefined)
      }
    })
  }

  const handleGoalChange = (goal: SetupGoal) => {
    clearAllGoalValues()
    if (GOAL_SELECTION_OPTIONS[goal].fieldName) {
      form.setFieldValue(GOAL_SELECTION_OPTIONS[goal].fieldName, currentTotalRiskGoalValue.inputValue)
    }
  }

  const totalTargetRiskConfig = GOAL_SELECTION_OPTIONS[SETUP_GOALS.TARGET_TOTAL_RISK]
  const minVolatilityConfig = GOAL_SELECTION_OPTIONS[SETUP_GOALS.MIN_VOLATILITY]
  const maxSpecificRiskConfig = GOAL_SELECTION_OPTIONS[SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK]

  return (
    <Box className="components_setup">
      <Typography color="inherit" noWrap className="setup-label">
        Setup
      </Typography>
      <Typography color="inherit" noWrap className="setup-section-label">
        Select Absolute Goal
      </Typography>
      <FormControl component="fieldset" fullWidth>
        <form.Field name="setup.goal">
          {(field) => (
            <RadioGroup
              value={field.state.value}
              onChange={(event) => {
                field.handleChange(event.target.value as typeof field.state.value)
                handleGoalChange(event.target.value as SetupGoal)
              }}
              className="setup-goal-radio-group"
            >
              <GoalRadio
                value={SETUP_GOALS.TARGET_TOTAL_RISK}
                label={totalTargetRiskConfig.label}
                fieldName="setup.targetTotalRiskValue"
                selectedValue={field.state.value}
                defaultValue={currentTotalRiskGoalValue.displayValue}
                status={totalRiskStatus}
                valueUnit={totalTargetRiskConfig.valueUnit}
                validator={targetTotalRiskSchema}
              />
              <GoalRadio
                value={SETUP_GOALS.MIN_VOLATILITY}
                label={minVolatilityConfig.label}
                selectedValue={field.state.value}
                defaultValue={currentTotalRiskGoalValue.displayValue}
                status={totalRiskStatus}
                valueUnit={minVolatilityConfig.valueUnit}
              />
              <FormControlLabel
                value={SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK}
                control={<Radio size="small" />}
                label={maxSpecificRiskConfig.label}
                className="setup-goal-radio-option"
              />
            </RadioGroup>
          )}
        </form.Field>
      </FormControl>
    </Box>
  )
}
